import { NewbuildingCarousel } from '@cian/frontend-newbuilding-carousel-component';
import { useBooleanState, useIntersectionObserver, useIsomorphicLayoutEffect } from '@cian/react-utils';

import { type FC, useCallback, useRef, useState } from 'react';

import { type MainCarouselBanner } from 'shared/types/mainCarousel';

import { Layout } from '../Layout';
import { NoImage } from '../NoImage';
import { type AnimationDuration, SliderProgressBar } from '../SliderProgressBar';

import { firstPaintOptions, mediaSettings } from './config';
import { Slide } from './internal/components/Slide';

import styles from './Carousel.css';

export type CarouselProps = {
  banners: MainCarouselBanner[];
  testId?: string;
  interval: AnimationDuration;
  regionId: number;
  onView: () => void;
  onDotClick: (index: number) => void;
  onSelectApartmentClick: (newbuildingId: number, index: number) => void;
};

export const Carousel: FC<CarouselProps> = ({
  banners,
  testId,
  interval,
  regionId,
  onView,
  onDotClick,
  onSelectApartmentClick,
}) => {
  const [currentSlide, setCurrentSlide] = useState(1);
  const { state: paused, setTrue: setPaused, setFalse: setNotPaused } = useBooleanState(false);
  const carouselRef = useRef<HTMLElement>(null);
  const currentRegionIdRef = useRef(regionId);
  const activeSlide = currentRegionIdRef.current === regionId ? currentSlide : 1;

  const { subscribeState } = useIntersectionObserver({
    deps: [onView],
    elementRef: carouselRef,
    observerOptions: { threshold: 0.5 },
    onInViewport: ({ subscribeState: currentSubscribeState }) => {
      currentSubscribeState.setSubscribeUnavailable();
      onView();
    },
  });

  useIsomorphicLayoutEffect(() => {
    currentRegionIdRef.current = regionId;
    setCurrentSlide(1);
    setNotPaused();
    subscribeState.setSubscribeAvailable();
  }, [regionId, setNotPaused, subscribeState]);

  const handleAnimationEnd = useCallback(
    () => setCurrentSlide(prev => (prev < banners.length ? prev + 1 : 1)),
    [banners.length],
  );

  if (!banners.length) {
    return (
      <section className={styles['carousel']} ref={carouselRef}>
        <NoImage />
      </section>
    );
  }

  return (
    <section className={styles['carousel']} data-testid={testId} ref={carouselRef}>
      <NewbuildingCarousel
        defaultCurrentSlide={activeSlide}
        firstPaintOptions={firstPaintOptions}
        media={mediaSettings}
        slides={banners.map((banner, index) => (
          <Slide
            active={index === activeSlide - 1}
            badges={banner.badges}
            disclaimer={banner.disclaimer}
            hasCommercialProperty={banner.hasCommercialProperty}
            imageUrl={banner.imageUrl}
            key={banner.id}
            label={banner.label}
            logoUrl={banner.logoUrl}
            newbuildingId={banner.id}
            publicUrl={banner.publicUrl}
            priority={index === 0}
            testId={`main-carousel-slide-${banner.id}`}
            title={banner.title}
            transports={banner.transports}
            video={banner.video}
            onSelectApartmentClick={() => onSelectApartmentClick(banner.id, index)}
            onTickerMouseOver={setPaused}
            onTickerMouseLeave={setNotPaused}
          />
        ))}
        onSlideChange={({ currentSlide: slideIndex }) => setCurrentSlide(slideIndex)}
      />

      {banners.length > 1 && (
        <div className={styles['progress-bar']}>
          <Layout>
            <div className={styles['progress-bar__wrapper']}>
              <SliderProgressBar
                activeIndex={activeSlide - 1}
                count={banners.length}
                duration={interval}
                paused={paused}
                onAnimationEnd={handleAnimationEnd}
                onDotClick={index => {
                  onDotClick(index);
                  setCurrentSlide(index + 1);
                }}
                onProgressBarHover={setPaused}
                onProgressBarLeave={setNotPaused}
              />
            </div>
          </Layout>
        </div>
      )}
    </section>
  );
};
